import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { data, Link, useFetcher } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { ForgotUsernameEmail } from '#app/components/email/ForgotUsernameEmail.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { findUserByUsernameOrEmail } from '#app/utils/user-validation.server.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/forgot-username.ts'

const ForgotUsernameSchema = z.object({
	email: EmailSchema,
})

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: ForgotUsernameSchema.superRefine(async (data, ctx) => {
			const user = await findUserByUsernameOrEmail(data.email)
			if (!user) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'No user exists with this email',
				})
				return
			}
		}),
		async: true,
	})
	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}
	const { email } = submission.value
	const user = await findUserByUsernameOrEmail(email)
	if (!user) {
		return data(
			{ result: submission.reply({ formErrors: ['User not found'] }) },
			{ status: 404 },
		)
	}

	const response = await sendEmail({
		to: email,
		subject: `Clearwater Farms 1 Username Recovery`,
		react: <ForgotUsernameEmail username={user.username} />,
	})

	if (response.status === 'success') {
		return redirectWithToast(
			'/username-emailed',
			{
				type: 'success',
				description: 'Check your email for your username',
			},
			{ status: 303 },
		)
	} else {
		return data(
			{ result: submission.reply({ formErrors: [response.error.message] }) },
			{ status: 500 },
		)
	}
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Username Recovery for Clearwater Farms 1' }]
}

export default function ForgotUsernameRoute() {
	const forgotUsername = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: 'forgot-username-form',
		constraint: getZodConstraint(ForgotUsernameSchema),
		lastResult: forgotUsername.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ForgotUsernameSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container pb-32 pt-20">
			<div className="flex flex-col justify-center">
				<div className="text-center">
					<h1 className="text-h1">Forgot Username</h1>
					<p className="mt-3 text-body-md text-muted-foreground">
						No worries, we'll send you an email.
					</p>
				</div>
				<div className="mx-auto mt-16 min-w-full max-w-sm sm:min-w-[368px]">
					<forgotUsername.Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<div>
							<Field
								labelProps={{
									htmlFor: fields.email.id,
									children: 'Email address',
								}}
								inputProps={{
									autoFocus: true,
									...getInputProps(fields.email, { type: 'text' }),
								}}
								errors={fields.email.errors}
							/>
						</div>
						<ErrorList errors={form.errors} id={form.errorId} />

						<div className="mt-6">
							<StatusButton
								className="w-full"
								status={
									forgotUsername.state === 'submitting'
										? 'pending'
										: (form.status ?? 'idle')
								}
								type="submit"
								disabled={forgotUsername.state !== 'idle'}
							>
								Recover username
							</StatusButton>
						</div>
					</forgotUsername.Form>
					<Button asChild variant="outline" className="">
						<Link
							to="/login"
							className="mt-2 text-center text-body-sm font-bold"
						>
							<Icon name="arrow-left" className="mr-2" />
							Back to Login
						</Link>
					</Button>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
